import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { IoMdMegaphone } from 'react-icons/io';
import { FiMail, FiAlertCircle, FiBarChart2, FiMessageCircle, FiPlus, FiSearch, FiChevronDown } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { AnnouncementRow, CommunicationsAnnouncementsResult } from '../../../model/CommunicationsAnnouncementsResult';
import api from '../../../api/api';
import { to } from '../../../util/util';

const categories = ['Regular announcement', 'Quarterly report', 'Half-yearly report', 'Annual report', 'Presentation', 'Notice of meeting'];
const pageSizes = [25, 50, 100];

const Announcements: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [result, setResult] = useState<CommunicationsAnnouncementsResult | null>(null);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('All');
    const [sensitive, setSensitive] = useState('Any');
    const [email, setEmail] = useState('Any');
    const [year, setYear] = useState('All');
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(1);

    useAsyncEffect(async () => {
        if (!stock?.id) return;
        setResult(null);
        const [err, data] = await to(api().getCommunicationsAnnouncements(stock.id));
        if (err) setError(err.message || 'Failed to load announcements');
        else { setError(''); setResult(data); }
    }, [stock?.id]);

    const rows = result?.rows || [];
    const years = useMemo(() => Array.from(new Set(rows.map(r => moment(r.date).format('YYYY')))).sort().reverse(), [rows]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return rows.filter(r =>
            (!q || r.headline.toLowerCase().includes(q)) &&
            (category === 'All' || r.category === category) &&
            (sensitive === 'Any' || (sensitive === 'Yes') === r.priceSensitive) &&
            (email === 'Any' || r.badge === email) &&
            (year === 'All' || moment(r.date).format('YYYY') === year));
    }, [rows, query, category, sensitive, email, year]);

    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const current = Math.min(page, pages);
    const visible = filtered.slice((current - 1) * pageSize, current * pageSize);
    const resetPage = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

    const pageList = (): (number | '…')[] => {
        if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
        const set = new Set([1, 2, 3, current - 1, current, current + 1, pages].filter(p => p >= 1 && p <= pages));
        const out: (number | '…')[] = [];
        Array.from(set).sort((a, b) => a - b).forEach((p, i, arr) => { if (i && p - arr[i - 1] > 1) out.push('…'); out.push(p); });
        return out;
    };

    const select = (label: string, value: string, options: string[], set: (v: string) => void) =>
        <label className="filter">
            <select value={value} onChange={e => set(e.target.value)}>{options.map(o => <option key={o} value={o}>{label}: {o}</option>)}</select>
            <FiChevronDown />
        </label>;

    const badge = (r: AnnouncementRow) => r.confirmUrl
        ? <a className={`pill ${r.tone}`} href={r.confirmUrl} target="_blank" rel="noopener noreferrer">{r.badge}</a>
        : <span className={`pill ${r.tone}`}>{r.badge}</span>;

    const Row = ({ r }: { r: AnnouncementRow }) => {
        const d = moment(r.date);
        return <div className="row">
            <div className="date"><div>{d.format('D MMM YYYY')}</div><div className="sub">{d.format('h:mm A')}</div></div>
            <div className="headline">
                <div className="h">{r.priceSensitive && <span className="dot" />}<a href={r.pdfUrl || r.asxUrl} target="_blank" rel="noopener noreferrer">{r.headline}</a></div>
                <div className="meta">
                    <span className={r.priceSensitive ? 'ps' : ''}>{r.priceSensitive ? 'Price sensitive' : 'Not price sensitive'}</span>
                    <span>· PDF{r.asxUrl && <> · <a href={r.asxUrl} target="_blank" rel="noopener noreferrer">ASX ↗</a></>}</span>
                    {r.tags.map(t => <span key={t} className="pill">{t}</span>)}
                </div>
            </div>
            <span><span className="pill category">{r.category}</span></span>
            <span className="num">{r.views.toLocaleString()}</span>
            <span className="num">{r.questions.toLocaleString()}</span>
            <span>{badge(r)}</span>
        </div>;
    };

    return <div className="announcements">
        <div className="page-header">
            <div>
                <h1>Announcements</h1>
                <p>Every ASX announcement for {stock?.companyName || stock?.ticker}, and the investor email that goes with it</p>
            </div>
            <a className="btn-primary" href="#"><FiPlus /> Add Announcement</a>
        </div>

        {error && <p className="error">{error}</p>}
        {!error && !result && <p className="loading">Loading announcements…</p>}

        {result && <>
            <div className="stats">
                <div className="stat">
                    <div className="h"><span className="icon-tile"><IoMdMegaphone /></span> This quarter</div>
                    <div className="v">{result.thisQuarter.toLocaleString()}</div>
                    <div className="d">announcements · {result.thisQuarterPriceSensitive.toLocaleString()} price-sensitive</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiMail /></span> Emailed to investors</div>
                    <div className="v">{result.emailed.toLocaleString()}</div>
                    <div className="d">of {result.total.toLocaleString()} · {result.autoSendLabel}</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiAlertCircle /></span> Waiting for approval
                        {result.waiting > 0 && <span className="pill warn">Action needed</span>}</div>
                    <div className="v">{result.waiting.toLocaleString()}</div>
                    <div className="d">{result.waitingDetail}</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiBarChart2 /></span> Views</div>
                    <div className="v">{result.views.toLocaleString()}</div>
                    <div className="d">across {result.total.toLocaleString()} announcements</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiMessageCircle /></span> Questions</div>
                    <div className="v">{result.questions.toLocaleString()}</div>
                    <div className="d">{(result.questions - result.questionsOpen).toLocaleString()} answered · {result.questionsOpen.toLocaleString()} waiting</div>
                </div>
            </div>

            <div className="card">
                <div className="filters">
                    <label className="search"><FiSearch /><input placeholder="Search headlines…" value={query} onChange={e => resetPage(setQuery)(e.target.value)} /></label>
                    {select('Category', category, ['All', ...categories], resetPage(setCategory))}
                    {select('Price Sensitive', sensitive, ['Any', 'Yes', 'No'], resetPage(setSensitive))}
                    {select('Email', email, ['Any', 'Emailed', 'Confirm to send', 'Not emailed'], resetPage(setEmail))}
                    {select('Year', year, ['All', ...years], resetPage(setYear))}
                    <span className="count">Sorted by date, newest first</span>
                </div>

                <div className="table">
                    <div className="row head">
                        <span>Date</span><span>Headline</span><span>Category</span><span className="num">Views</span><span className="num">Questions</span><span>Investor email</span>
                    </div>
                    {visible.map(r => <Row key={r.sendGuid || `${r.date}${r.headline}`} r={r} />)}
                    {visible.length === 0 && <p className="empty">No announcements match</p>}
                </div>

                <div className="footer">
                    <span className="count">Showing {filtered.length ? (current - 1) * pageSize + 1 : 0}–{Math.min(current * pageSize, filtered.length)} of {filtered.length.toLocaleString()} announcements</span>
                    <label className="filter rows">
                        <select value={pageSize} onChange={e => resetPage(setPageSize)(Number(e.target.value))}>{pageSizes.map(n => <option key={n} value={n}>Rows per page: {n}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <div className="pagination">
                        <button type="button" disabled={current === 1} onClick={() => setPage(current - 1)}>‹</button>
                        {pageList().map((p, i) => p === '…' ? <span key={`e${i}`}>…</span>
                            : <button type="button" key={p} className={p === current ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>)}
                        <button type="button" disabled={current === pages} onClick={() => setPage(current + 1)}>›</button>
                    </div>
                </div>
            </div>
        </>}
    </div>;
};

export default Announcements;
